
import {
  takeEvery, put, call, takeLatest,
} from 'redux-saga/effects';

import {
  MODALS, API_URLS, FETCH_OPTIONS,
} from '../constants';
import { requestGet, requestPost } from '../utils/request';
import { aGetFeedSettingsRequest, aGetTennisFeedSettingsRequest, aUpdateFeedStatus, aUpdateOddsStatus, aUpdateTennisFeedStatus, aUpdateTennisOddsStatus } from '../reducers';
import { aSetIsFetching, aSetOpenModal } from '../reducers/common';
import { aGetFeedSettingsSuccess, aGetTennisFeedSettingsSuccess } from '../reducers/feedSettings';

/**
 * @namespace sagas/feedSettings
 *
 */
/**
 * @memberof sagas/feedSettings
 * @typedef getFeedSettings/response.data
 * @type {object}
 * @property {User} user
 * @property {string} sessionId
 */
/**
 * @memberof sagas/feedSettings
 * @async
 */
function* getFeedSettingsSaga() {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_FEED_SETTINGS,
      value: true,
    }));

    response = yield call(requestGet, API_URLS.GET_FEED_SETTINGS, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetFeedSettingsSuccess({
        feedSettings: response.data.feedPusherSettings,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_FEED_SETTINGS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_FEED_SETTINGS,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/feedSettings
 * @typedef getTennisFeedSettings/response.data
 * @type {object}
 * @property {User} user
 * @property {string} sessionId
 */
/**
 * @memberof sagas/feedSettings
 * @async
 */
function* getTennisFeedSettingsSaga() {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_FEED_SETTINGS,
      value: true,
    }));

    const response = yield call(requestGet, API_URLS.GET_TENNIS_FEED_SETTINGS, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const feedSettingsData = response.data?.map((feedSettingItem) => (
        {
          leagueId: feedSettingItem.competitionId,
          feedStatus: feedSettingItem.feedStatus,
          invalidateOdds: feedSettingItem.invalidateOddsStatus,
        }
      ));
      yield put(aGetTennisFeedSettingsSuccess({
        feedSettings: feedSettingsData,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_FEED_SETTINGS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_TENNIS_FEED_SETTINGS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* updateFeedStatusSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UPDATE_FEED_STATUS,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.UPDATE_FEED_STATUS, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.UPDATE_FEED_STATUS,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UPDATE_FEED_STATUS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* updateTennisFeedStatusSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UPDATE_TENNIS_FEED_STATUS,
      value: true,
    }));

    const body = {
      competitionId: action.payload.leagueId,
      feedStatus: action.payload.feedStatus,
    };

    const response = yield call(requestPost, API_URLS.UPDATE_TENNIS_FEED_STATUS, body, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.UPDATE_TENNIS_FEED_STATUS,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UPDATE_TENNIS_FEED_STATUS,
      value: false,
    }));
  }
}
/**
 * @param {object} action
 */
function* updateOddsStatusSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UPDATE_ODDS_STATUS,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.UPDATE_ODDS_STATUS, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.UPDATE_ODDS_STATUS,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UPDATE_ODDS_STATUS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* updateTennisOddsStatusSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UPDATE_TENNIS_ODDS_STATUS,
      value: true,
    }));

    const body = {
      competitionId: action.payload.leagueId,
      invalidateOddsStatus: action.payload.invalidateOdds,
    };

    const response = yield call(requestPost, API_URLS.UPDATE_TENNIS_ODDS_STATUS, body, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.UPDATE_TENNIS_ODDS_STATUS,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UPDATE_TENNIS_ODDS_STATUS,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/feedSettings
 * @async
 * @returns {void}
 */
export default function* feedSettingsSaga() {
  yield takeEvery(aGetFeedSettingsRequest, getFeedSettingsSaga);
  yield takeLatest(aUpdateFeedStatus, updateFeedStatusSaga);
  yield takeLatest(aUpdateOddsStatus, updateOddsStatusSaga);

  yield takeEvery(aGetTennisFeedSettingsRequest, getTennisFeedSettingsSaga);
  yield takeLatest(aUpdateTennisFeedStatus, updateTennisFeedStatusSaga);
  yield takeLatest(aUpdateTennisOddsStatus, updateTennisOddsStatusSaga);
}
